// @greenlight-spec: 48-user-passthrough.md#per-user-oauth
// @greenlight-spec: 28-it-dashboard-ui.md
// @greenlight-spec: 19-design-system.md
// @greenlight-spec: decisions/0086-a-click-before-the-provider.md
import * as React from 'react';
import { Link, Navigate, useSearchParams } from 'react-router-dom';
import { AlertTriangle, ArrowRight, CheckCircle2, Plug } from 'lucide-react';
import type { ConnectTarget } from '@greenlight/shared';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { AppIconTile } from '@/components/greenlight/AppIconTile';
import { Eyebrow } from '@/components/greenlight/Eyebrow';
import { IntegrationTile } from '@/components/greenlight/IntegrationTile';
import { Logo } from '@/components/greenlight/Logo';
import { ThemeToggle } from '@/components/ThemeToggle';
import { Skeleton } from '@/components/ui/skeleton';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';

/** Set by `/connect/start` and cleared by the callback; left behind when the provider never sent the browser back. */
const PENDING_COOKIE = 'gl_connect_pending';

function pendingIntegration(): string | null {
  const entry = document.cookie.split('; ').find((c) => c.startsWith(`${PENDING_COOKIE}=`));
  return entry ? decodeURIComponent(entry.slice(PENDING_COOKIE.length + 1)) : null;
}

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * The page every `connect_url` opens, laid out like an OAuth consent screen:
 * which app (with its owner) wants to use which of the person's accounts, and
 * one button. Only that click, through
 * `/connect/start`, sends the browser to the provider, so a broken registration
 * can never trap someone on the provider's error page on every visit. Rendered
 * outside the AppShell, like `/connect/result`, because the person is
 * mid-task in an app.
 *
 * Nothing from the query string becomes a link as-is: Connect and Not now are
 * rebuilt from the integration id and credential slug, and the return goes
 * through `/connect/return`, where the server confines the destination.
 */
export function ConnectPage(): React.JSX.Element {
  const [params] = useSearchParams();
  const { loading, unauthenticated } = useAuth();
  const integrationId = params.get('integration');
  const credential = params.get('credential');
  const returnTo = params.get('return_to');
  const valid = integrationId !== null && UUID.test(integrationId) && !!credential;

  const queryKey = ['connect-target', integrationId, credential, returnTo];
  const target = api.userConnections.connectTarget.useQuery(
    queryKey,
    {
      query: {
        integration: integrationId ?? '',
        credential: credential ?? '',
        ...(returnTo ? { return_to: returnTo } : {}),
      },
    },
    { queryKey, enabled: valid && !loading && !unauthenticated, retry: false },
  );

  const [pending, setPending] = React.useState<boolean>(false);
  // Shown once, then cleared: someone who backed out of the provider's sign-in
  // also leaves the cookie, and should not be told about it twice.
  React.useEffect(() => {
    const found = integrationId !== null && pendingIntegration() === integrationId;
    setPending(found);
    if (found) document.cookie = `${PENDING_COOKIE}=; Path=/connect; Max-Age=0`;
  }, [integrationId]);

  React.useEffect(() => {
    document.title = 'Connect your account · Greenlight';
    return () => {
      document.title = 'Greenlight';
    };
  }, []);

  if (unauthenticated) {
    return <Navigate to={`/login?return_to=${encodeURIComponent(window.location.href)}`} replace />;
  }

  const startHref =
    valid && integrationId && credential
      ? `/connect/start?integration=${encodeURIComponent(integrationId)}&credential=${encodeURIComponent(credential)}${returnTo ? `&return_to=${encodeURIComponent(returnTo)}` : ''}`
      : null;
  const back = returnTo ? `/connect/return?to=${encodeURIComponent(returnTo)}` : null;
  const data = target.data?.status === 200 ? target.data.body : null;
  const name = data?.integration.name ?? 'this integration';
  const app = data?.app?.name ?? null;
  const appName = app ?? 'This app';

  let body: React.ReactNode;
  const settled = !loading && !target.isPending;
  if (!valid || (settled && !data)) {
    body = (
      <Card
        header={
          <Glyph>
            <AlertTriangle className="size-6 text-muted-foreground" aria-hidden />
          </Glyph>
        }
      >
        <Eyebrow>Connect your account</Eyebrow>
        <h1 className="text-[22px] font-semibold leading-tight tracking-[-0.02em]">
          This connection isn’t available
        </h1>
        <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
          Greenlight has no account connection to make here. The app may be pointing at an
          integration IT has not set up; tell IT which app you were using.
        </p>
        <Actions back={back} />
      </Card>
    );
  } else if (!data) {
    body = (
      <Card
        header={
          <Glyph>
            <Plug className="size-6 text-muted-foreground" aria-hidden />
          </Glyph>
        }
      >
        <div className="space-y-3" aria-busy="true" aria-label="Loading">
          <Skeleton className="mx-auto h-3 w-24" />
          <Skeleton className="mx-auto h-6 w-52" />
          <Skeleton className="h-11 w-full" />
        </div>
      </Card>
    );
  } else if (data.status === 'active') {
    body = (
      <Card header={<Parties target={data} connected />}>
        <Eyebrow>Connect your account</Eyebrow>
        <h1 className="text-[22px] font-semibold leading-tight tracking-[-0.02em]">
          You’re connected to {name}
        </h1>
        <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
          {appName} can already use your {name} account. If it still can’t reach {name}, connecting
          again replaces the old connection.
        </p>
        <div className="mt-6 flex flex-col gap-2">
          {back ? (
            <Button asChild className="h-11 w-full">
              <a href={back}>Back to {app ?? 'the app'}</a>
            </Button>
          ) : (
            <Button asChild className="h-11 w-full">
              <Link to="/">Open dashboard</Link>
            </Button>
          )}
          {startHref && (
            <Button asChild variant="outline" className="h-11 w-full">
              <a href={startHref}>Connect again</a>
            </Button>
          )}
        </div>
      </Card>
    );
  } else {
    const again = data.status === 'needs_reauth';
    const heading = again
      ? `Reconnect your ${name} account`
      : app
        ? `${app} wants to access your ${name} account`
        : `Connect your ${name} account`;
    body = (
      <Card header={<Parties target={data} />}>
        <Eyebrow>Connect your account</Eyebrow>
        <h1 className="text-[22px] font-semibold leading-tight tracking-[-0.02em]">{heading}</h1>
        <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
          {again
            ? `Your ${name} connection stopped working, which happens after a password change or a reset by IT. `
            : `It can only see and do what you already can in ${name}. `}
          You’ll sign in there to allow it, then come straight back to {app ?? 'where you were'}.
        </p>
        {pending && (
          <Alert className="mt-4 text-left" data-testid="connect-pending">
            <AlertDescription>
              Last time, {name} didn’t send you back to Greenlight. If you saw an error there, IT
              needs to fix the {name} setup. Tell them: “{app ?? 'the app'} can’t connect to {name}
              ”, and the error you saw.
            </AlertDescription>
          </Alert>
        )}
        <div className="mt-6 flex flex-col gap-2">
          {startHref && (
            <Button asChild className="h-11 w-full">
              <a href={startHref}>
                {pending ? 'Try again' : again ? 'Reconnect' : `Continue to ${name}`}
              </a>
            </Button>
          )}
          <Button asChild variant="outline" className="h-11 w-full">
            {back ? <a href={back}>Not now</a> : <Link to="/">Not now</Link>}
          </Button>
        </div>
      </Card>
    );
  }

  return (
    <div
      className="relative flex min-h-[100dvh] flex-col items-center justify-center overflow-x-hidden bg-background px-5 py-12"
      data-testid="connect-page"
      data-status={data?.status ?? (valid ? 'loading' : 'invalid')}
    >
      <div
        aria-hidden
        className="pointer-events-none fixed -top-[200px] left-1/2 h-[600px] w-[900px] -translate-x-1/2 bg-[radial-gradient(circle,hsl(var(--signal)/0.14)_0%,transparent_62%)]"
      />
      <div className="fixed right-4 top-4 z-10">
        <ThemeToggle />
      </div>

      <main className="relative z-[1] flex w-full max-w-[408px] flex-col items-center">
        <div className="mb-7 flex flex-col items-center gap-2">
          <Logo className="h-7 text-foreground" />
          <div className="font-mono text-[10px] uppercase tracking-[0.06em] text-muted-foreground">
            by Shift AI
          </div>
        </div>
        {body}
      </main>
    </div>
  );
}

function Card({ header, children }: { header: React.ReactNode; children: React.ReactNode }) {
  return (
    <div className="w-full rounded-gl-lg border border-border bg-card p-6 text-center shadow-xl sm:p-8">
      {header}
      {children}
    </div>
  );
}

function Glyph({ children }: { children: React.ReactNode }) {
  return (
    <div className="mx-auto mb-4 flex size-11 items-center justify-center rounded-full bg-muted">
      {children}
    </div>
  );
}

/**
 * Who is asking for what: the app's tile and owner on the left, the
 * integration's mark on the right. With no app behind `return_to` (a local run,
 * an IT test) only the integration shows.
 */
function Parties({ target, connected = false }: { target: ConnectTarget; connected?: boolean }) {
  const integration = (
    <figure className="flex w-28 flex-col items-center gap-2" data-testid="connect-integration">
      <IntegrationTile
        slug={target.integration.slug}
        displayName={target.integration.name}
        catalogKey={target.integration.catalog_key}
        size={56}
      />
      <figcaption className="text-sm font-medium leading-tight">
        {target.integration.name}
      </figcaption>
    </figure>
  );
  if (!target.app) return <div className="mb-5 flex justify-center">{integration}</div>;
  return (
    <div className="mb-5 flex items-start justify-center gap-3">
      <figure className="flex w-28 flex-col items-center gap-2" data-testid="connect-app">
        <AppIconTile iconUrl={target.app.icon_url} name={target.app.name} size={56} radius={14} />
        <figcaption className="text-sm leading-tight">
          <span className="block font-medium">{target.app.name}</span>
          {target.app.owner_name && (
            <span className="mt-0.5 block text-xs text-muted-foreground wrap-anywhere">
              by {target.app.owner_name}
            </span>
          )}
        </figcaption>
      </figure>
      <div className="flex h-14 items-center text-muted-foreground" aria-hidden>
        {connected ? (
          <CheckCircle2 className="size-5 text-verdant" />
        ) : (
          <ArrowRight className="size-5" />
        )}
      </div>
      {integration}
    </div>
  );
}

function Actions({ back }: { back: string | null }) {
  return (
    <div className="mt-6 flex flex-col gap-2">
      <Button asChild variant="outline" className="h-11 w-full">
        {back ? <a href={back}>Back to the app</a> : <Link to="/">Open dashboard</Link>}
      </Button>
    </div>
  );
}
